import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useEffect, useContext } from 'react'
import { throttle } from 'lodash'
import {
  CMasthead,
  CMastheadFragments,
  CMastheadBlock,
  CSeomatic,
  SeomaticData,
  CBackgroundImageCopy,
  CBackgroundImageCopyBlock,
  CBackgroundImageCopyFragments,
  CImageCopyBlocks,
  CImageCopyBlocksBlock,
  CImageCopyBlocksFragments,
  CCtaBox,
  CCtaBoxBlock,
  CCtaBoxFragments,
  CAboutVideo,
  CComparisonTable,
  CCounter,
  CTestimonialsSlider,
  CFindADoctor,
  CCenteredCopy,
  CStats,
  CHero,
  CHeroFragment,
  CHeroBlock,
  CCtaBlockFragment,
  CCtaBlockBlock,
  CCtaBlock,
  CContentWithSideImage,
  CContentWithSideImageBlock,
  CStepsFragment,
  CStepsBlock,
  CSteps,
  CStatisticsSectionFragment,
  CStatisticsSectionBlock,
  CStatisticsSection,
  CCtaContactFragment,
  CCtaContactBlock,
  CCtaContact,
  CCtaColumnsFragment,
  CCtaColumnsBlock,
  CCtaColumns,
  CExpandableFaqs,
  CExpandableFaqsFragments,
  CExpandableFaqsBlock,
  CContentWithSideImageFragment,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { HeaderContext } from 'ui/context/header'
import classNames from 'classnames'
import { gql } from '@urql/core'
import {
  CFeaturedItems,
  CFeaturedItemsBlock,
  CFeaturedItemsFragment,
} from 'ui/components/organisms/c-featured-items'
import { getSiteInfo, useSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')
  const isUS = siteInfo.iso === 'en-US'

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_lens_Entry {
            masthead {
              ...masthead
            }
            backgroundImageCopy {
              ...backgroundImageCopy
            }
            backgroundImageCopy2 {
              ...backgroundImageCopy2
            }
            imageCopyBlocks {
              ...imageCopyBlocks
            }
            ctaBox {
              ...ctaBox
            }
            hero {
              ...hero
            }
            featuredItems {
              ...featuredItems
            }
            ctaBlock {
              ...ctaBlock
            }
            contentWithSideImage {
              ...contentWithSideImage
            }
            steps {
              ...steps
            }
            statisticsSection {
              ...statisticsSection
            }
            ctaContact {
              ...ctaContact
            }
             ctaColumns {
              ...ctaColumns
            }
            expandableFaqs {
              ...expandableFaqs
            }
          }
        }
      }
      ${CHeroFragment}
      ${CFeaturedItemsFragment}
      ${CCtaBlockFragment}
      ${CContentWithSideImageFragment.default}
      ${CStepsFragment}
      ${CStatisticsSectionFragment}
      ${CCtaContactFragment}
      ${CCtaColumnsFragment}
      ${CExpandableFaqsFragments.field}
      ${CMastheadFragments.field}
      ${CBackgroundImageCopyFragments.field}
      ${CBackgroundImageCopyFragments.field2}
      ${CImageCopyBlocksFragments.field}
      ${CCtaBoxFragments.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data?.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      transparentHeader: !isUS,
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    hero: Array<CHeroBlock>
    featuredItems: Array<CFeaturedItemsBlock>
    backgroundImageCopy: Array<CBackgroundImageCopyBlock>
    backgroundImageCopy2: Array<CBackgroundImageCopyBlock>
    imageCopyBlocks: Array<CImageCopyBlocksBlock>
    ctaBox: Array<CCtaBoxBlock>
    ctaBlock: Array<CCtaBlockBlock>
    contentWithSideImage: Array<CContentWithSideImageBlock>
    steps: Array<CStepsBlock>
    statisticsSection: Array<CStatisticsSectionBlock>
    ctaContact: Array<CCtaContactBlock>
    ctaColumns: Array<CCtaColumnsBlock>
    expandableFaqs: Array<CExpandableFaqsBlock>
  }
  global: CraftGlobals
  transparentHeader: true
}

export default function Lens({ entry, global }: Props) {
  const headerContext = useContext(HeaderContext)
  const siteInfo = useSiteInfo()

  const handleWindowScroll = () => {
    const computeIsClear: boolean =
      window.pageYOffset < (window.innerWidth > 678 ? 280 : 200)
    headerContext?.setIsClear(computeIsClear)
  }

  const handleWindowScrollThrottled = throttle(handleWindowScroll, 100)

  useEffect(() => {
    if (!siteInfo.isUS) {
      window.addEventListener('scroll', handleWindowScrollThrottled)
    }

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
    }
  }, [])

  useEffect(() => {
    if (siteInfo.isUS) {
      headerContext?.setIsClear(false)
    }
  }, [headerContext?.isClear, siteInfo.isUS])

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      {siteInfo.isUS ? (
        <CHero field={entry.hero} />
      ) : (
        <CMasthead field={entry.masthead} />
      )}
      {siteInfo.isUS && (
        <>
          <CFeaturedItems field={entry.featuredItems} />
          <CComparisonTable field={global.comparisonTable} isUs />
          <CCtaBlock field={entry.ctaBlock} />
          <CContentWithSideImage field={entry.contentWithSideImage} />
          <CSteps field={entry.steps} />
          <CStatisticsSection field={entry.statisticsSection} />
          <CTestimonialsSlider field={global.testimonialsSlider} />
          <CCtaContact field={entry.ctaContact} />
          <CCtaColumns field={entry.ctaColumns} />
          <CExpandableFaqs field={entry.expandableFaqs} />
        </>
      )}
      {!siteInfo.isUS && (
        <>
          <CBackgroundImageCopy field={entry.backgroundImageCopy} />
          <CBackgroundImageCopy field={entry.backgroundImageCopy2} />
          <CImageCopyBlocks field={entry.imageCopyBlocks} />
          <CAboutVideo field={global?.aboutVideo} />
          <div
            className={classNames({
              'hidden md:block': siteInfo.isUS,
            })}
          >
            <CComparisonTable field={global?.comparisonTable} />
          </div>
          <CCounter field={global?.counter} />
          <CTestimonialsSlider field={global?.testimonialsSlider} />
          <CCtaBox field={entry.ctaBox} />
          <CFindADoctor field={global?.findADoctor} />
        </>
      )}
      <CCenteredCopy field={global?.centeredCopy} />
    </DefaultLayout>
  )
}
